import { Suspense, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useMe } from '../api/me';
import { useJob, useJobs } from '../api/jobs';
import { StateBadge } from './StateBadge';
import { RelativeTime } from './RelativeTime';
import { RunsList } from './RunsList';
import { Skeleton } from './Skeleton';
import { JiraLink } from './JiraLink';
import { JobActions } from './JobActions';
import { PromptBody } from './PromptBody.lazy';
import { ApiError } from '../api/client';
import { errorMessage } from '../lib/errorMessage';
import type { Job } from '../types';

export function JobDrawer() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const close = () => void navigate('/');
  const panelRef = useRef<HTMLElement>(null);

  // Reactive list-cache read: `useJobs` subscribes to the jobs-list query that
  // `useUserJobsSubscription` (SSE) writes into via setQueryData, so an open
  // drawer re-renders when the job's state changes. (A render-time
  // `queryClient.getQueriesData` snapshot here carried no subscription — the
  // table behind the drawer updated on SSE writes while the drawer went stale.)
  // The drawer renders under JobsListLayout, where `useMe` and the jobs-list
  // query are already mounted, so this adds no fetches; the per-job fetch
  // below still only fires when the job isn't in the list (cold deep link).
  const { data: me } = useMe();
  const { data: jobs } = useJobs(me?.id);
  const cachedFromList = jobs?.find((j) => j.id === id);

  const { data: fetched, error, isPending } = useJob(cachedFromList ? undefined : id);
  const job = cachedFromList ?? fetched;

  // Esc to close. `navigate` is referenced directly (not via `close`) so the
  // dep list is honest without an exhaustive-deps disable; react-router keeps
  // its identity stable, so the listener is still mounted once in practice.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') void navigate('/');
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  // Modal focus management: move focus into the dialog on open and restore it to
  // whatever was focused before (typically the triggering row) when it closes.
  useEffect(() => {
    const previouslyFocused = document.activeElement as HTMLElement | null;
    panelRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, []);

  return (
    <div className="fixed inset-0 z-40">
      <button
        aria-label="Close drawer"
        onClick={close}
        className="absolute inset-0 bg-slate-900/40"
      />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="job-drawer-title"
        tabIndex={-1}
        className="absolute right-0 top-0 flex h-full w-full max-w-xl flex-col bg-white shadow-xl outline-none"
      >
        <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4">
          <h2 id="job-drawer-title" className="text-lg font-semibold text-slate-900">
            Job
          </h2>
          <button
            type="button"
            onClick={close}
            className="rounded border border-slate-300 px-2 py-1 text-sm text-slate-600 hover:border-slate-400"
          >
            Close
          </button>
        </div>

        <div className="flex-1 overflow-auto p-6">
          {!job && isPending && <DrawerSkeleton />}
          {error && !job && <DrawerError error={error} onClose={close} />}
          {job && (
            <>
              <DrawerHeader job={job} />
              <h3 className="mt-6 mb-3 text-sm font-semibold uppercase tracking-wide text-slate-500">
                Runs
              </h3>
              <RunsList jobId={job.id} />
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function DrawerHeader({ job }: { job: Job }) {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <StateBadge state={job.state} />
        <span className="text-xs uppercase tracking-wide text-slate-500">{job.mode ?? '—'}</span>
        <span className="text-xs uppercase tracking-wide text-slate-500">{job.source}</span>
      </div>
      <JobActions job={job} />
      <h3 className="text-base font-semibold text-slate-900">{job.repo ?? '(no repo)'}</h3>
      {job.prompt && (
        <Suspense
          fallback={
            <p className="line-clamp-3 whitespace-pre-wrap text-sm text-slate-700">{job.prompt}</p>
          }
        >
          <PromptBody prompt={job.prompt} />
        </Suspense>
      )}
      {job.resolutionReason && (
        <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <span className="font-semibold text-slate-600">Resolution: </span>
          {job.resolutionReason}
        </div>
      )}
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-500">
        <dt>Created</dt>
        <dd className="text-slate-700">
          <RelativeTime iso={job.createdAt} />
        </dd>
        <dt>Updated</dt>
        <dd className="text-slate-700">
          <RelativeTime iso={job.updatedAt} />
        </dd>
        <dt>Job ID</dt>
        <dd className="font-mono text-slate-700">{job.id}</dd>
        {job.mode === 'INTERACTIVE' && (
          <>
            <dt>Volume</dt>
            <dd className="font-mono text-slate-700">
              {job.volume ? job.volume.id : <span className="text-slate-400">none yet</span>}
            </dd>
          </>
        )}
        {job.jiraIssueKey && (
          <>
            <dt>Jira</dt>
            <dd>
              <JiraLink
                issueKey={job.jiraIssueKey}
                className="text-sky-700 underline-offset-2 hover:underline"
              />
            </dd>
          </>
        )}
      </dl>
    </div>
  );
}

function DrawerSkeleton() {
  return (
    <div className="space-y-3">
      <Skeleton variant="block" className="h-6 w-1/3" />
      <Skeleton variant="block" className="h-4 w-2/3" />
      <Skeleton variant="block" className="h-4 w-1/2" />
    </div>
  );
}

function DrawerError({ error, onClose }: { error: unknown; onClose: () => void }) {
  if (error instanceof ApiError && error.status === 404) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="mb-2">Job not found.</p>
        <button type="button" onClick={onClose} className="underline">
          Back to list
        </button>
      </div>
    );
  }
  if (error instanceof ApiError && error.status === 403) {
    return (
      <div className="rounded border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        <p className="mb-2">You don&apos;t have access to this job.</p>
        <button type="button" onClick={onClose} className="underline">
          Back to list
        </button>
      </div>
    );
  }
  return (
    <div className="rounded border border-red-200 bg-red-50 p-4 text-sm text-red-800">
      Couldn&apos;t load job: {errorMessage(error)}
    </div>
  );
}
